import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Vertrag, Tier, ZAHLUNGSINTERVALL } from '../types';
import vertragService from '../services/vertragService';
import tierService from '../services/tierService';
import { Card, Badge, LoadingScreen, Icon, Button, Field, Input, DateInput, Select, Textarea, FormActions } from '../components/ui';
import DokumentSektion from '../components/DokumentSektion';
import VertragsartikelSektion from '../components/VertragsartikelSektion';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { ZurueckLink } from '../components/ZurueckLink';

const intervallLabels: Record<string, string> = {
  MONATLICH: 'Monatlich', QUARTALSWEISE: 'Quartalsweise', HALBJAEHRLICH: 'Halbjährlich', JAEHRLICH: 'Jährlich'
};

// ISO (yyyy-mm-dd) -> dd.mm.yyyy, ohne Zeitzonen-Verschiebung.
const fmtDatum = (iso?: string): string => {
  if (!iso) return '–';
  const [y, m, d] = iso.split('T')[0].split('-');
  return (y && m && d) ? `${d}.${m}.${y}` : iso;
};
const isoDay = (iso?: string) => (iso ? iso.split('T')[0] : '');
const heute = () => new Date().toISOString().split('T')[0];

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-gray-100 py-2.5 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-right text-sm font-medium text-gray-900">{value ?? '–'}</span>
  </div>
);

const VertragDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [vertrag, setVertrag] = useState<Vertrag | null>(null);
  const [tier, setTier] = useState<Tier | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<false | 'edit' | 'beenden'>(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>({});

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const v = await vertragService.getById(parseInt(id));
      setVertrag(v);
      // Der Vertrag gehört zu genau einem Tier (falls verknüpft).
      const t = v.tierId ? await tierService.getById(v.tierId).catch(() => null) : null;
      setTier(t);
    } catch { toast.error('Vertrag konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!vertrag) return;
    setForm({
      unterschriftsdatum: isoDay(vertrag.unterschriftsdatum),
      laufzeitJahre: vertrag.laufzeitJahre,
      zahlungsintervall: vertrag.zahlungsintervall,
      autoVerlaengerung: vertrag.autoVerlaengerung,
      verlaengerungJahre: vertrag.verlaengerungJahre,
      notizen: vertrag.notizen || '',
    });
    setEditing('edit');
  };
  const startBeenden = () => { setForm({ endeDatum: heute() }); setEditing('beenden'); };
  const cancel = () => { setEditing(false); setForm({}); };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vertrag) return;
    setSaving(true);
    try {
      await vertragService.update(vertrag.id, {
        kundeId: vertrag.kundeId, grabId: vertrag.grabId, tierId: vertrag.tierId,
        unterschriftsdatum: form.unterschriftsdatum, laufzeitJahre: parseInt(form.laufzeitJahre) || 1,
        autoVerlaengerung: form.autoVerlaengerung, verlaengerungJahre: parseInt(form.verlaengerungJahre) || 1,
        zahlungsintervall: form.zahlungsintervall, notizen: form.notizen || undefined, active: vertrag.active,
      });
      markiereNeu(`vertrag-${vertrag.id}`);
      toast.success('Vertrag gespeichert'); cancel(); await load();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Vertrag konnte nicht gespeichert werden'); }
    finally { setSaving(false); }
  };

  const saveBeenden = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vertrag) return;
    setSaving(true);
    try {
      await vertragService.beenden(vertrag.id, { endeDatum: form.endeDatum });
      markiereNeu(`vertrag-${vertrag.id}`);
      toast.success(`Vertrag ${vertrag.vertragsnummer} beendet`); cancel(); await load();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Vertrag konnte nicht beendet werden'); }
    finally { setSaving(false); }
  };

  if (loading) return <LoadingScreen />;
  if (!vertrag) return <div className="p-6">Vertrag nicht gefunden. <Link to="/kunden" className="text-brand-600">Zurück</Link></div>;

  const beendet = vertrag.status === 'BEENDET';
  const neu = ankunft(`vertrag-${vertrag.id}`);

  return (
    <div className="mx-auto max-w-3xl p-6">
      {/* Ohne Vorgeschichte (direkt aufgerufen) zum Kunden des Vertrags. */}
      <ZurueckLink
        fallback={vertrag.kundeId ? `/kunden/${vertrag.kundeId}` : '/kunden'}
        fallbackLabel={vertrag.kundeId ? 'Zurück zum Kunden' : 'Zurück zu den Kunden'}
      />

      <div className="mb-6 mt-3 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Vertrag {vertrag.vertragsnummer}</h1>
        <Badge key={vertrag.status} className={`${beendet ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-800'} ${neu.className && 'ui-wechsel'}`}>
          {beendet ? 'Beendet' : 'Aktiv'}
        </Badge>
        {vertrag.autoVerlaengerung && <Badge className="bg-blue-100 text-blue-800">Auto-Verlängerung</Badge>}
        {!editing && (
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={startEdit} icon={<Icon.Edit className="h-4 w-4" />}>Bearbeiten</Button>
            {!beendet && <Button size="sm" variant="secondary" onClick={startBeenden}>Vertrag beenden</Button>}
          </div>
        )}
      </div>

      <Card ref={neu.ref} className={`mb-6 p-6 ${neu.className}`}>
        <h2 className="mb-3 text-base font-semibold text-gray-900">Vertragsdaten</h2>

        {editing === 'edit' ? (
          <form onSubmit={saveEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Unterschrift" required><DateInput required value={form.unterschriftsdatum} onChange={v => setForm({ ...form, unterschriftsdatum: v })} /></Field>
              <Field label="Laufzeit (Jahre)" required><Input type="number" min="1" required value={form.laufzeitJahre} onChange={e => setForm({ ...form, laufzeitJahre: e.target.value })} /></Field>
            </div>
            <Field label="Zahlungsintervall"><Select value={form.zahlungsintervall} onChange={e => setForm({ ...form, zahlungsintervall: e.target.value })}>{ZAHLUNGSINTERVALL.map(z => <option key={z} value={z}>{intervallLabels[z] || z}</option>)}</Select></Field>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.autoVerlaengerung} onChange={e => setForm({ ...form, autoVerlaengerung: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
              Automatische Verlängerung
            </label>
            {form.autoVerlaengerung && (
              <Field label="Verlängerung (Jahre)"><Input type="number" min="1" value={form.verlaengerungJahre} onChange={e => setForm({ ...form, verlaengerungJahre: e.target.value })} /></Field>
            )}
            <Field label="Notizen"><Textarea rows={2} value={form.notizen} onChange={e => setForm({ ...form, notizen: e.target.value })} /></Field>
            <FormActions>
              <Button type="button" variant="secondary" onClick={cancel}>Abbrechen</Button>
              <Button type="submit" loading={saving}>Speichern</Button>
            </FormActions>
          </form>
        ) : editing === 'beenden' ? (
          <form onSubmit={saveBeenden} className="space-y-3 rounded-lg bg-amber-50 p-3">
            <p className="text-xs text-amber-900">Vertrag beenden. Sind danach keine aktiven Verträge mehr am zugehörigen Grab, wird es wieder „Frei“. Die Tierdaten bleiben als Historie erhalten.</p>
            <Field label="Enddatum" required><DateInput required value={form.endeDatum} onChange={v => setForm({ ...form, endeDatum: v })} /></Field>
            <FormActions>
              <Button type="button" variant="secondary" onClick={cancel}>Abbrechen</Button>
              <Button type="submit" loading={saving}>Vertrag beenden</Button>
            </FormActions>
          </form>
        ) : (
          <>
            <Row label="Vertragsnummer" value={vertrag.vertragsnummer} />
            <Row label="Kunde" value={vertrag.kundeId
              ? <Link to={`/kunden/${vertrag.kundeId}`} className="text-brand-700 hover:underline">{`${vertrag.kundeName ?? ''} (${vertrag.kundennummer ?? '–'})`}</Link>
              : `${vertrag.kundeName ?? ''} (${vertrag.kundennummer ?? '–'})`} />
            <Row label="Grab" value={vertrag.grabId
              ? <Link to={`/graeber/${vertrag.grabId}`} className="text-brand-700 hover:underline">{`${vertrag.grabNummer ?? ''} – ${vertrag.friedhofName ?? ''}`}</Link>
              : `${vertrag.grabNummer ?? ''} – ${vertrag.friedhofName ?? ''}`} />
            <Row label="Unterschriftsdatum" value={fmtDatum(vertrag.unterschriftsdatum)} />
            <Row label="Status" value={beendet ? 'Beendet' : 'Aktiv'} />
            {vertrag.endeDatum && <Row label="Beendet am" value={fmtDatum(vertrag.endeDatum)} />}
            <Row label="Laufzeit" value={`${vertrag.laufzeitJahre} Jahre`} />
            <Row label="Automatische Verlängerung" value={vertrag.autoVerlaengerung ? `Ja (+${vertrag.verlaengerungJahre} Jahre)` : 'Nein'} />
            <Row label="Zahlungsintervall" value={intervallLabels[vertrag.zahlungsintervall] || vertrag.zahlungsintervall} />
            <Row label="Notizen" value={vertrag.notizen} />
          </>
        )}
      </Card>

      {/* Abzurechnende Leistungen (Preis + MwSt als Snapshot der Preisliste) */}
      <div className="mb-6">
        <VertragsartikelSektion vertrag={vertrag} />
      </div>

      <Card className="p-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Beigesetztes Tier</h2>
          {vertrag.grabId && <Link to={`/graeber/${vertrag.grabId}`} className="text-sm font-medium text-brand-700 hover:text-brand-900">Zum Grab</Link>}
        </div>
        {tier ? (
          <>
            <Row label="Name" value={tier.name} />
            <Row label="Art / Rasse" value={[tier.art, tier.rasse].filter(Boolean).join(' / ') || '–'} />
            <Row label="Geboren" value={fmtDatum(tier.gebDatum)} />
            <Row label="Gestorben" value={fmtDatum(tier.sterbeDatum)} />
            <Row label="Gewicht" value={tier.gewicht != null ? `${tier.gewicht} kg` : undefined} />
            <Row label="Größe" value={tier.groesse} />
            <Row label="Beisetzung" value={fmtDatum(tier.beisetzungsdatum)} />
          </>
        ) : (
          <p className="text-sm text-gray-500">Mit diesem Vertrag ist kein Tier verknüpft.</p>
        )}
      </Card>

      {/* Dokumente */}
      <div className="mt-6">
        <DokumentSektion entityType="VERTRAG" entityId={vertrag.id} />
      </div>
    </div>
  );
};

export default VertragDetail;
